import React, { useState } from 'react';
import { Mail, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

/**
 * Soft email-verification banner.
 *
 * Shows above the main content for any logged-in user whose `email_verified`
 * flag is false. Lets them resend the verification email or dismiss for the
 * current session. The account is fully functional regardless — this is just
 * a visibility nudge so reminder emails actually land in valid inboxes.
 */
export const EmailVerificationBanner = () => {
  const { user, resendVerification } = useAuth();
  const [dismissed, setDismissed] = useState(
    typeof window !== 'undefined' && sessionStorage.getItem('hb_verify_banner_dismissed') === '1'
  );
  const [busy, setBusy] = useState(false);

  if (!user || user.email_verified || dismissed) return null;
  // Admins/superadmins don't see the verification banner — they manage the
  // platform and any email they get from us is internal anyway.
  if (user.role === 'admin' || user.role === 'superadmin') return null;

  const handleResend = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await resendVerification();
      toast.success('Verificatielink opnieuw verstuurd. Check je inbox!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kon verificatielink niet versturen.');
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = () => {
    sessionStorage.setItem('hb_verify_banner_dismissed', '1');
    setDismissed(true);
  };

  return (
    <div
      className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between gap-3 text-sm"
      data-testid="email-verification-banner"
    >
      <div className="flex items-center gap-2 text-amber-900 flex-1 min-w-0">
        <Mail className="w-4 h-4 shrink-0" />
        <span className="truncate">
          Bevestig je e-mailadres ({user.email}) zodat we je kunnen bereiken.
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={handleResend}
          disabled={busy}
          className="underline text-amber-900 font-medium hover:text-amber-700 disabled:opacity-50"
          data-testid="resend-verification-btn"
        >
          {busy ? 'Versturen…' : 'Verstuur opnieuw'}
        </button>
        <button
          onClick={handleDismiss}
          className="text-amber-800 hover:text-amber-900"
          aria-label="Banner sluiten"
          data-testid="dismiss-verification-banner"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default EmailVerificationBanner;
